"use strict";
var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.HelperText = exports.FieldError = exports.Label = exports.TextField = exports.Form = void 0;
var styled_1 = __importDefault(require("@emotion/styled"));
var react_aria_components_1 = require("react-aria-components");
var theme_1 = require("../../theme");
/**
 * A form provides a way to collect and submit data from users.
 * It handles form submission, validation, and accessibility.
 *
 * @see {@link https://react-spectrum.adobe.com/react-aria/Form.html}
 * @see {@link https://compass.circleci.com/?path=/story/rac-form--default}
 *
 * @example
 * ```tsx
 * <Form onSubmit={handleSubmit}>
 *   <TextField>
 *     <Label>Username</Label>
 *     <Input />
 *     <HelperText>Enter your username</HelperText>
 *   </TextField>
 * </Form>
 * ```
 *
 * @param onSubmit - Handler that is called when the form is submitted
 * @param children - The form fields to display
 * @param id - The element's unique identifier
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed description
 */
exports.Form = (0, styled_1.default)(react_aria_components_1.Form)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  gap: ", ";\n"], ["\n  display: flex;\n  flex-direction: column;\n  gap: ", ";\n"])), theme_1.theme.space.space24);
/**
 * A text field is an input that allows users to enter text.
 * It can include a label, helper text, and error message.
 *
 * @example
 * ```tsx
 * <TextField>
 *   <Label>Username</Label>
 *   <Input />
 *   <HelperText>Enter your username</HelperText>
 * </TextField>
 * ```
 *
 * @param isDisabled - Whether the text field is disabled
 * @param isReadOnly - Whether the text field is read only
 * @param isRequired - Whether the text field is required
 * @param isInvalid - Whether the text field is invalid
 * @param children - The label, input, and helper text components
 * @param id - The element's unique identifier
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed description
 */
exports.TextField = (0, styled_1.default)(react_aria_components_1.TextField)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  display: flex;\n  flex-direction: column;\n  gap: ", ";\n"], ["\n  display: flex;\n  flex-direction: column;\n  gap: ", ";\n"])), theme_1.theme.space.space8);
/**
 * A label provides a text label for a form control.
 * It is automatically associated with the control and provides accessibility support.
 *
 * @example
 * ```tsx
 * <Label>Username</Label>
 * ```
 *
 * @param children - The label text
 * @param id - The element's unique identifier
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed description
 */
exports.Label = (0, styled_1.default)(react_aria_components_1.Label)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  color: ", ";\n  font-size: ", ";\n  font-weight: ", ";\n  line-height: ", ";\n"], ["\n  color: ", ";\n  font-size: ", ";\n  font-weight: ", ";\n  line-height: ", ";\n"])), theme_1.theme.colors.n900, theme_1.theme.fontSizes.fontsize16, theme_1.theme.fontWeights.medium, theme_1.theme.lineHeights.lineheight24);
/**
 * A field error displays validation error messages for a form field.
 * It is automatically shown when the field is invalid.
 *
 * @example
 * ```tsx
 * <TextField isInvalid>
 *   <Label>Username</Label>
 *   <Input />
 *   <FieldError>Username is required</FieldError>
 * </TextField>
 * ```
 *
 * @param children - The error message to display
 * @param id - The element's unique identifier
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed description
 */
exports.FieldError = (0, styled_1.default)(react_aria_components_1.FieldError)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  color: ", ";\n  font-size: ", ";\n  line-height: ", ";\n"], ["\n  color: ", ";\n  font-size: ", ";\n  line-height: ", ";\n"])), theme_1.theme.colors.red500, theme_1.theme.fontSizes.fontsize14, theme_1.theme.lineHeights.lineheight20);
/**
 * Helper text provides additional context or instructions for a form field.
 * It is displayed below the field and can be used to provide hints or examples.
 *
 * @example
 * ```tsx
 * <TextField>
 *   <Label>Username</Label>
 *   <Input />
 *   <HelperText>Enter your username</HelperText>
 * </TextField>
 * ```
 *
 * @param children - The helper text to display
 * @param id - The element's unique identifier
 * @param aria-label - Defines a string value that labels the current element
 * @param aria-labelledby - Identifies the element (or elements) that labels the current element
 * @param aria-describedby - Identifies the element (or elements) that describes the object
 * @param aria-details - Identifies the element (or elements) that provide a detailed description
 */
exports.HelperText = (0, styled_1.default)(react_aria_components_1.Text)(templateObject_5 || (templateObject_5 = __makeTemplateObject(["\n  color: ", ";\n  font-size: ", ";\n  line-height: ", ";\n"], ["\n  color: ", ";\n  font-size: ", ";\n  line-height: ", ";\n"])), theme_1.theme.colors.n500, theme_1.theme.fontSizes.fontsize14, theme_1.theme.lineHeights.lineheight20);
var templateObject_1, templateObject_2, templateObject_3, templateObject_4, templateObject_5;
//# sourceMappingURL=Form.js.map